import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/AuthSlice/authSlice';
import { LogOut } from 'lucide-react';

import JobsIconSVG      from '../../assets/images/Sidebar/SVG_Functions/JobsIconSVG';
import HSIconSVG        from '../../assets/images/Sidebar/SVG_Functions/HSIconSVG';
import NetworkIconSVG   from '../../assets/images/Sidebar/SVG_Functions/NetworkIconSVG';
import SavedJobIconSVG  from '../../assets/images/Sidebar/SVG_Functions/SavedJobIconSVG';
import FeedbackIconSVG  from '../../assets/images/Sidebar/SVG_Functions/FeedbackIconSVG';
import SupportIconSVG   from '../../assets/images/Sidebar/SVG_Functions/SupportIconSVG';
import MyNetworkIconSVG from '../../assets/images/Sidebar/SVG_Functions/MyNetworkIconSVG';
import InboxIconSVG     from '../../assets/images/Sidebar/SVG_Functions/InboxIconSVG';
import FeedIconSVG      from '../../assets/images/Sidebar/SVG_Functions/FeedIconSVG';
import SubIconSVG       from '../../assets/images/Sidebar/SVG_Functions/SubIconSVG';
import CreditsIconSVG  from '../../assets/images/Sidebar/SVG_Functions/CreditsIconSVG';
import ReferIconSVG    from '../../assets/images/Sidebar/SVG_Functions/ReferIconSVG';
import AIIconSVG        from '../../assets/images/Sidebar/SVG_Functions/AIIconSVG';
import DailyTaskIconSVG from '../../assets/images/Sidebar/SVG_Functions/DailyTaskIconSVG';
// ─── helpers ────────────────────────────────────────────────────────────────

function isPathActive(current, target) {
    if (!target) return false;
    if (target === '/') return current === '/';
    return current === target || current.startsWith(`${target}/`);
}

function capitalize(str) {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function getPlanLabel(tier) {
    if (!tier) return 'Free Trial';
    const t = String(tier).toLowerCase();
    return t === 'free' ? 'Free Trial' : `${capitalize(t)} Plan`;
}

function getTrialInfo(user) {
    const tier = user?.subscription?.tier || 'free';
    if (tier !== 'free') return null;

    const trialEnd = user?.trialEndsAt ? new Date(user.trialEndsAt) : null;
    if (!trialEnd || Number.isNaN(trialEnd.getTime())) return null;

    const msLeft  = trialEnd.getTime() - Date.now();
    if (msLeft <= 0) return { daysLeft: 0, pct: 0, ended: true };

    const TRIAL_DAYS = 7;
    const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
    const pct      = Math.round((daysLeft / TRIAL_DAYS) * 100);
    return { daysLeft, pct, ended: false };
}


function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [query]);
    return matches;
}

// ─── nav structure ───────────────────────────────────────────────────────────

const NAV_GROUPS = [
    {
        label: 'Dashboard',
        items: [
            { name: 'Jobs',           icon: JobsIconSVG,      path: '/' },
            { name: 'AI Assistant',   icon: AIIconSVG,        path: '/ai-assistant' },
            { name: 'Daily Tasks',    icon: DailyTaskIconSVG, path: '/dailytasks' },
            { name: 'Daily Digest',   icon: FeedIconSVG,      path: '/daily-digest' },
            // TheirStack hiring signals — hidden from the sidebar, not removed.
            // The route and the page still work, so anything already linking to
            // /hiringsignal (saved signals, daily-task sprints) keeps resolving.
            // { name: 'Hiring Signals', icon: HSIconSVG,        path: '/hiringsignal', pulse: true },
            { name: 'Hiring Signals (US)', icon: HSIconSVG,   path: '/hiring-signals-us' },
            { name: 'Saved Jobs & Signals', icon: SavedJobIconSVG, path: '/savedjobs' },
        ],
    },
    {
        label: 'Network',
        items: [
            { name: 'Networking',  icon: NetworkIconSVG,   path: '/networking' },
            { name: 'My Network',  icon: MyNetworkIconSVG, path: '/my-network' },
            { name: 'Inbox',       icon: InboxIconSVG,     path: '/inbox' },
            { name: 'Feed',        icon: FeedIconSVG,      path: '/feed' },
        ],
    },
    {
        label: 'Settings',
        items: [
            { name: 'Profile',      icon: MyNetworkIconSVG, path: '/profile' },
            { name: 'Subscription', icon: SubIconSVG,        path: '/subscription' },
            { name: 'Credits & Usage', icon: CreditsIconSVG, path: '/credits' },
            { name: 'Refer & Earn', icon: ReferIconSVG,     path: '/refer' },
            { name: 'Feedback',     icon: FeedbackIconSVG,  path: '/feedback' },
            { name: 'Support',      icon: SupportIconSVG,   path: '/support' },
        ],
    },
];

// ─── mobile bottom bar items ─────────────────────────────────────────────────

const MOBILE_ITEMS = [
    { name: 'Jobs',    icon: JobsIconSVG,      path: '/' },
    { name: 'AI',      icon: AIIconSVG,        path: '/ai-assistant' },
    { name: 'Tasks',   icon: DailyTaskIconSVG, path: '/dailytasks' },
    { name: 'Network', icon: NetworkIconSVG,   path: '/networking' },
    { name: 'Saved',   icon: SavedJobIconSVG,  path: '/savedjobs' },
    { name: 'Profile', icon: MyNetworkIconSVG, path: '/profile' },
];

// ─── desktop sidebar ─────────────────────────────────────────────────────────

const Sidebar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery('(max-width: 640px)');
    const user            = useSelector((s) => s.auth.user);
    const onboardingActive = useSelector((s) => s.auth.onboardingActive);

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [logoutConfirm, setLogoutConfirm] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
    };

    // ── mobile bottom bar ───────────────────────────────────────────────────
    if (isMobile) {
        return (
            <>
                <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E6E8EE] pb-safe">
                    <ul className="flex items-stretch h-[60px]">
                        {MOBILE_ITEMS.map((item) => {
                            const active = isPathActive(location.pathname, item.path);
                            return (
                                <li key={item.name} className="flex-1">
                                    <Link
                                        to={item.path}
                                        className="flex flex-col items-center justify-center h-full gap-0.5 relative"
                                    >
                                        {active && (
                                            <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full" style={{ background: '#3F57E0' }} />
                                        )}
                                        <item.icon className={`w-[18px] h-[18px] ${active ? 'text-[#3F57E0]' : 'text-[#94A3B8]'}`} />
                                        <span className={`text-[10px] font-medium font-dmsans ${active ? 'text-[#3F57E0]' : 'text-[#94A3B8]'}`}>
                                            {item.name}
                                        </span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

            </>
        );
    }

    // ── desktop sidebar ─────────────────────────────────────────────────────
    return (
        <>
            <nav className="flex flex-col h-full w-full bg-white overflow-y-auto overflow-x-hidden text-left">

                {/* nav groups */}
                <div className="flex-1 px-3 py-3 space-y-0.5">
                    {NAV_GROUPS.map((group, gi) => (
                        <section key={group.label}>
                            {/* section label */}
                            <div className={`px-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#94A3B8] font-dmsans ${gi > 0 ? 'pt-4' : 'pt-2'}`}>
                                {group.label}
                            </div>

                            <ul className="space-y-0.5">
                                {group.items.map((item) => {
                                    const active = isPathActive(location.pathname, item.path);

                                    // ─ regular link item ─
                                    return (
                                        <li key={item.name}>
                                            <Link
                                                to={item.path}
                                                className={`flex items-center gap-2.5 px-[10px] py-[8px] rounded-[10px] text-[13px] transition-colors duration-150 group
                                                    ${active
                                                        ? 'bg-[#EEF1FE] text-[#3F57E0] font-semibold'
                                                        : 'font-medium text-[#334155] hover:bg-[#F4F5F8] hover:text-[#0F172A]'}`}
                                            >
                                                    <item.icon
                                                    className={`w-4 h-4 flex-shrink-0 ${active ? 'text-[#3F57E0]' : 'text-[#94A3B8] group-hover:text-[#334155]'}`}
                                                />
                                                <span className="leading-none font-dmsans">{item.name}</span>
                                                {item.pulse && user?.initialHiringSignalScan && (
                                                    <span
                                                        className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                        style={{ background: '#F59E0B', boxShadow: '0 0 0 4px rgba(245,158,11,0.18)' }}
                                                    />
                                                )}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    ))}
                    {/* Log out */}
                    <div className="pt-2 mt-1 border-t border-[#EFF1F5]">
                        <button
                            type="button"
                            onClick={() => setLogoutConfirm(true)}
                            className="flex items-center gap-2.5 w-full px-[10px] py-[8px] rounded-[10px] text-[13px] font-medium font-dmsans text-red-500 hover:bg-red-50 transition-colors"
                        >
                            <LogOut className="w-4 h-4 flex-shrink-0" />
                            <span className="leading-none">Log out</span>
                        </button>
                    </div>
                </div>

                {/* ── user profile card ─────────────────────────────────── */}
                {!onboardingActive && <div className="px-3 pb-3 mt-auto">

                    <div className="border-t border-[#EFF1F5] pt-2.5">


                        {/* user card button */}
                        <button
                            type="button"
                            onClick={() => navigate('/profile')}
                            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#F2F4F8] transition-colors"
                            style={{ background: '#FAFBFD' }}
                        >
                            {/* avatar */}
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ background: 'linear-gradient(135deg, #3F57E0, #6377F0)' }}
                            >
                                <span className="text-white text-xs font-bold font-dmsans">
                                    {(user?.name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                                </span>
                            </div>

                            <div className="flex-1 min-w-0 text-left">
                                <p className="text-[12.5px] font-semibold text-[#0F172A] font-dmsans truncate leading-tight">
                                    {user?.name || 'My Account'}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span
                                        className="inline-flex items-center text-[9.5px] font-bold uppercase tracking-[0.04em] font-dmsans px-1.5 py-0.5 rounded-md"
                                        style={{ background: '#EEF1FE', color: '#3F57E0' }}
                                    >
                                        {getPlanLabel(user?.subscription?.tier)}
                                    </span>
                                    {(() => {
                                        const trial = getTrialInfo(user);
                                        if (!trial) return null;
                                        const urgent = trial.daysLeft <= 2;
                                        return (
                                            <span
                                                className="inline-flex items-center text-[9.5px] font-bold font-dmsans px-1.5 py-0.5 rounded-md"
                                                style={{
                                                    background: urgent ? '#FEF2F2' : '#F0FDF4',
                                                    color: urgent ? '#F97066' : '#16A34A',
                                                }}
                                            >
                                                {trial.ended ? 'Ended' : `${trial.daysLeft}d left`}
                                            </span>
                                        );
                                    })()}
                                </div>
                                <p className="text-[10.5px] text-[#94A3B8] font-dmsans truncate leading-tight mt-0.5">
                                    {user?.email}
                                </p>
                            </div>

                        </button>
                    </div>
                </div>}
            </nav>

            {/* ── Logout confirmation modal ──────────────────────────────── */}
            {logoutConfirm && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)' }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-left">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#FEF2F2' }}>
                            <LogOut size={18} className="text-red-500" />
                        </div>
                        <h3 className="text-[16px] font-bold text-[#0F172A] font-dmsans mb-1">Log out?</h3>
                        <p className="text-[13px] text-[#64748B] font-dmsans leading-relaxed mb-6">You'll be signed out of your Career Champ account on this device.</p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setLogoutConfirm(false)}
                                className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-[#334155] border border-[#E6E8EE] rounded-xl bg-white hover:bg-[#F6F7FB] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => { setLogoutConfirm(false); handleLogout(); }}
                                className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-white rounded-xl bg-red-500 hover:bg-red-600 transition-colors"
                            >
                                Log out
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default Sidebar;
